import moment from 'moment'
import 'moment/locale/sv'
import { IVolym3IdagDel, IVolym3Manad } from '../../gen/api'

// Celltext, procent och avrundning i Volymuppföljning (pejlen-produkt#107, A21).
// Procenten räknas på oavrundade tal. Timmar visas som heltal i cellerna och med en decimal i
// Beställd tid i dag. Vanlig avrundning, inte uppåt.

// Beställd tid under en halv minut i en månad räknas som ingen beställning, så att procenten inte skenar
const MINSTA_BESTALLDA_TIMMAR = 0.01

const enDecimalFormat = new Intl.NumberFormat('sv-SE', { minimumFractionDigits: 1, maximumFractionDigits: 1 })
const heltalFormat = new Intl.NumberFormat('sv-SE', { maximumFractionDigits: 0 })

export const enDecimal = (timmar: number) => enDecimalFormat.format(timmar)

export const heltal = (timmar: number) => heltalFormat.format(Math.round(timmar))

export const utfortTotalt = (m: IVolym3Manad) => m.utfordPlanerad + m.utfordOplanerad

export const harBestallning = (m: IVolym3Manad) => m.bestalld >= MINSTA_BESTALLDA_TIMMAR

/** Utförandegraden i hela procent, eller null när månaden saknar beställning */
export const procent = (m: IVolym3Manad): number | null => (harBestallning(m) ? Math.round((100 * utfortTotalt(m)) / m.bestalld) : null)

/** Cellens text enligt A21: utförd tid och utförandegraden inom parentes. Beställd tid står i tooltipen. */
export const celltext = (m: IVolym3Manad | undefined): string => {
  if (!m) return '–'
  const utfort = utfortTotalt(m)
  if (harBestallning(m)) return `${heltal(utfort)} h (${procent(m)} %)`
  if (utfort > 0) return `${heltal(utfort)} h (–)`
  return '–'
}

/**
 * Sortering i en månadskolumn på samma procent som visas (F50). Celler utan procent hamnar sist,
 * både stigande och fallande. antd vänder resultatet vid fallande ordning, därför sortOrder.
 */
export const jamforProcent = (a: number | null, b: number | null, sortOrder: 'ascend' | 'descend' | null | undefined) => {
  if (a === null && b === null) return 0
  if (a === null) return sortOrder === 'descend' ? -1 : 1
  if (b === null) return sortOrder === 'descend' ? 1 : -1
  return a - b
}

const sv = (datum: string, format: string) => moment(datum, format).locale('sv')

/** "1 mar 2026" */
export const datumtext = (datum: string) => sv(datum, 'YYYY-MM-DD').format('D MMM YYYY')

/** "Augusti 2026" */
export const manadsnamn = (ym: string) => {
  const text = sv(ym, 'YYYY-MM').format('MMMM YYYY')
  return text.charAt(0).toUpperCase() + text.slice(1)
}

/** "aug 2026", till kolumnrubriker och listan Bortvalda kolumner */
export const kortManad = (ym: string) => sv(ym, 'YYYY-MM').format('MMM YYYY')

/** Månadens beställda tid per insatskategori, utan det som inte räknas (A22, Anton 2026-10-08) */
export const bestalltPerKategori = (m: IVolym3Manad): Array<{ kategori: string; timmar: number }> => {
  const perKategori = new Map<string, number>()
  for (const d of m.delar) {
    if (d.raknasInte) continue
    perKategori.set(d.kategori, (perKategori.get(d.kategori) || 0) + d.timmar)
  }
  return Array.from(perKategori.entries())
    .map(([kategori, timmar]) => ({ kategori, timmar }))
    .sort((a, b) => a.kategori.localeCompare(b.kategori))
}

export interface IdagKategori {
  kategori: string
  taktManad: number
  /** Den tidigaste starten bland kategorins gällande beställningar */
  fran: string
  /** Det första slutdatumet, alltså när något i kategorin löper ut härnäst. null = tills vidare */
  till: string | null
  /** Den senaste ändringsbeställningen på någon av kategorins beställningar. null = ingen */
  andrad: string | null
}

/**
 * Beställd tid i dag per insatskategori, i timmar per månad, utan det som inte räknas (A2, A9, Anton 2026-10-08).
 * Datumen gäller beställningar, inte kategorier, så de slås ihop: tidigaste Från, första Till och senaste Ändrad.
 */
export const bestalltIdagPerKategori = (delar: IVolym3IdagDel[]): IdagKategori[] => {
  const perKategori = new Map<string, IdagKategori>()
  for (const d of delar) {
    if (d.raknasInte) continue
    const k = perKategori.get(d.kategori)
    if (!k) {
      perKategori.set(d.kategori, { kategori: d.kategori, taktManad: d.taktManad, fran: d.fran, till: d.till, andrad: d.andrad })
      continue
    }
    k.taktManad += d.taktManad
    if (d.fran < k.fran) k.fran = d.fran
    if (d.till !== null && (k.till === null || d.till < k.till)) k.till = d.till
    if (d.andrad !== null && (k.andrad === null || d.andrad > k.andrad)) k.andrad = d.andrad
  }
  return Array.from(perKategori.values()).sort((a, b) => a.kategori.localeCompare(b.kategori))
}

/**
 * Slutdatum som text. Som kolumnen Trygghetslarm i Mina ärenden: utan slutdatum, eller med ett slutdatum mer än
 * cirka 15 år fram (ett platshållardatum i Combine), visas "Tillsvidare".
 */
export const tillText = (till: string | null, idag: string) => {
  if (!till) return 'Tillsvidare'
  return moment(till, 'YYYY-MM-DD').diff(moment(idag, 'YYYY-MM-DD').add(5, 'y'), 'years') < 10 ? datumtext(till) : 'Tillsvidare'
}

/**
 * Är månadens utförda tid inte färdigregistrerad? Det senaste registrerade besöket ligger före månadens
 * sista dag. Gäller innevarande månad, men också förra månaden de första dagarna i en ny månad.
 */
export const ejFardigregistrerad = (ym: string, senasteBesok: string | null) =>
  !!senasteBesok && senasteBesok < moment(ym, 'YYYY-MM').endOf('month').format('YYYY-MM-DD')
